import useDashboardsAccess from '../../../../frontend/dashboard/hooks/useDashboardsAccess';
import { useDeviceManagerAccess } from '../../../../frontend/device_management/shared/hooks/useDeviceManagerAccess';
import useAssetManagerAccess from '../../../../frontend/equipment_management/shared/hooks/useAssetManagerAccess';
import { useFraudWatchAccess } from '../../../../frontend/global/modules/FraudWatch/shared/hooks/useFraudWatchAccess';
import useEmbeddedReportsAccess from '../../../../frontend/global/modules/PowerBiReports/shared/hooks/useEmbeddedReportsAccess';
import { useHealthcareAccess } from '../../../../frontend/healthcare/hooks/useHealthcareAccess';
import { useInventoryPageAccess } from '../../../../frontend/inventory_management/shared/hooks/useInventoryPageAccess';
import useShipmentManagementAccess from '../../../../frontend/shipment_manager/hooks/useShipmentManagementAccess';
import useRiskMonitorAccess from '../../../../frontend/suv/shared/hooks/useRiskMonitorAccess';
import { PACK } from '../../consts/packs';
import { PAGE } from '../../consts/pages';

export type DefaultPage =
  | PAGE.assetManager
  | PAGE.commandCenterDashboard
  | PAGE.deviceManager
  | PAGE.fraudWatch
  | PAGE.powerBiReports
  | PAGE.sentinel
  | PAGE.shipments
  | PAGE.healthcareMedicalDevices
  | PAGE.healthcareDashboard
  | PAGE.inventory;

interface DefaultPageConfig {
  name: DefaultPage;
  pack: PACK;
  link: string;
  title: string;
  hasAccess: boolean;
}

export const useDefaultPages = () => {
  const { hasDashboardsAccess } = useDashboardsAccess();
  const { hasDMAccess } = useDeviceManagerAccess();
  const { hasAMAccess } = useAssetManagerAccess();
  const { hasRMAccess } = useRiskMonitorAccess();
  const { hasFraudWatchAccess } = useFraudWatchAccess();
  const { hasSMAppAccess } = useShipmentManagementAccess();
  const { hasEmbeddedReportsAccess } = useEmbeddedReportsAccess();
  const { hasHealthcareAccess } = useHealthcareAccess();
  const { hasInventoryManagementAccess } = useInventoryPageAccess();

  const getAllDefaultPages = (): Record<DefaultPage, DefaultPageConfig> => {
    return {
      [PAGE.assetManager]: {
        name: PAGE.assetManager,
        pack: PACK.equipment,
        link: '',
        title: 'navlink_assetManager',
        hasAccess: hasAMAccess()
      },
      [PAGE.commandCenterDashboard]: {
        name: PAGE.commandCenterDashboard,
        pack: PACK.dashboard,
        link: '',
        title: 'navlink_commandCenterDashboard',
        hasAccess: hasDashboardsAccess()
      },
      [PAGE.deviceManager]: {
        name: PAGE.deviceManager,
        pack: PACK.deviceManagement,
        link: 'devices',
        title: 'navlink_deviceAndTagManagement',
        hasAccess: hasDMAccess()
      },
      [PAGE.fraudWatch]: {
        name: PAGE.fraudWatch,
        pack: PACK.global,
        link: 'fraudwatch',
        title: 'navlink_fraudwatch',
        hasAccess: hasFraudWatchAccess()
      },
      [PAGE.healthcareMedicalDevices]: {
        name: PAGE.healthcareMedicalDevices,
        pack: PACK.healthcare,
        link: 'medical-devices',
        title: 'navlink_medicalDevices',
        hasAccess: hasHealthcareAccess(),
      },
      [PAGE.healthcareDashboard]: {
        name: PAGE.healthcareDashboard,
        pack: PACK.healthcare,
        link: 'dashboard',
        title: 'navlink_healthcareDashboard',
        hasAccess: hasHealthcareAccess(),
      },
      [PAGE.powerBiReports]: {
        name: PAGE.powerBiReports,
        pack: PACK.global,
        link: 'embedded-reports',
        title: 'navlink_powerBiReports',
        hasAccess: hasEmbeddedReportsAccess()
      },
      [PAGE.sentinel]: {
        name: PAGE.sentinel,
        pack: PACK.suv,
        link: '',
        title: 'navlink_sentinel',
        hasAccess: hasRMAccess()
      },
      [PAGE.shipments]: {
        name: PAGE.shipments,
        pack: PACK.simpl2,
        link: 'shipments',
        title: 'navlink_shipmentManager',
        hasAccess: hasSMAppAccess()
      },
      [PAGE.inventory]: {
        name: PAGE.inventory,
        pack: PACK.inventoryManagement,
        link: '',
        title: 'navlink_inventory',
        hasAccess: hasInventoryManagementAccess()
      },
    };
  };

  const getAccessibleDefaultPages = (): DefaultPageConfig[] => {
    return Object.values(getAllDefaultPages()).filter(page => page.hasAccess);
  };

  const getDefaultPageByName = (name: DefaultPage): DefaultPageConfig | undefined => {
    return getAllDefaultPages()[name];
  };

  return {
    getAllDefaultPages,
    getAccessibleDefaultPages,
    getDefaultPageByName,
  };

};